import React from "react";
import { FaTimes, FaCog } from "react-icons/fa";

const VideoResolutionModal = ({
  isOpen,
  onClose,
  resolution,
  setResolution,
  isFreeModel,
  mode = 'pro',
  isPreview = false,
  disable1080p = false,
  disable720p = false,
  selectedAIModel = null,
  reelDuration = null,
  className = ""
}) => {
  if (!isOpen && !isPreview) return null;
  
  // All resolution options - 1080p will be disabled in Pro mode
  const allOptions = [
    // { id: '480p', label: '480p' },
    { id: '720p', label: '720p' },
    { id: '1080p', label: '1080p' }
  ];
  
  // Add 768p option for minimax_hailuo-2.3-fast model
  const isMinimaxHailuoFast = selectedAIModel === 'minimax_hailuo-2.3-fast';
  const options = isMinimaxHailuoFast 
    ? [{ id: '768p', label: '768p' }, { id: '1080p', label: '1080p' }]
    : allOptions;
  
  // Check if 1080p should be disabled due to model limitations
  const is1080pDisabledByModel = selectedAIModel === 'google_veo-3.1-fast';
  
  // For minimax_hailuo-2.3-fast: 1080p is disabled when duration is 10s (10s only available for 768p)
  const is1080pDisabledByDuration = isMinimaxHailuoFast && reelDuration === 10;

  const Panel = (
    <div className="glass-effect rounded-2xl border border-white/10 bg-[#111111]/95 backdrop-blur-2xl p-4 w-72 sm:w-80">
      <div className="flex items-center gap-2 mb-2">
        <FaCog className="text-amber-300/90 text-xs" />
        <h3 className="text-xs font-medium text-white">Resolution</h3>
        {!isPreview && (
          <button type="button" className="ml-auto text-gray-300 hover:text-white" onClick={onClose}>
            <FaTimes className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map(r => {
          const disabledDueToCredits = (disable1080p && r.id === '1080p') || (disable720p && r.id === '720p');
          const disabledDueToModel = (is1080pDisabledByModel && r.id === '1080p');
          const disabledDueToDuration = (is1080pDisabledByDuration && r.id === '1080p');
          const isDisabled = disabledDueToCredits || disabledDueToModel || disabledDueToDuration;
          const disabledReason = disabledDueToDuration
            ? 'not available for 10s duration (10s only available for 768p)'
            : disabledDueToModel 
              ? 'not available for this model' 
              : disabledDueToCredits 
                ? 'insufficient credits' 
                : '';
          const tooltipMessage = disabledDueToDuration
            ? '1080p is not available for 10s duration. 10 seconds is only available for 768p resolution.'
            : disabledDueToModel 
              ? '1080p is not available for google_veo-3.1-fast. Use google_veo-3.1 for 1080p support.'
              : disabledDueToCredits
                ? `${r.label} ${disabledReason}`
                : r.label;
          return (
            <div key={r.id} className="flex flex-col items-start relative group">
              <button
                type="button"
                className={`px-2 py-1 rounded-full border text-[11px] ${
                  isDisabled
                    ? 'bg-gray-800/30 text-gray-500 border-gray-700/40 cursor-not-allowed opacity-50'
                    : resolution === r.id 
                      ? 'bg-white/10 text-white border-white/20' 
                      : 'text-gray-300 border-gray-600/40 hover:bg-white/5'
                }`}
                onClick={() => { 
                  if (!isDisabled) {
                    setResolution(r.id); 
                    if (!isPreview) onClose(); 
                  }
                }}
                disabled={isDisabled}
                title={tooltipMessage}
              >
                {r.label}
              </button>
              {disabledDueToDuration && (
                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 pointer-events-none">
                  <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2.5 py-1.5 shadow-2xl max-w-[16rem] text-[10px] leading-snug text-white relative">
                    <div className="font-medium mb-0.5">1080p Not Available</div>
                    <div className="text-gray-300">1080p is not available for 10s duration. 10 seconds is only available for 768p resolution.</div>
                    <div className="absolute left-3 -bottom-0.5 w-1.5 h-1.5 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                  </div>
                </div>
              )}
              {disabledDueToModel && (
                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 pointer-events-none">
                  <div className="rounded-lg border border-white/10 bg-[#111111]/95 backdrop-blur-2xl px-2.5 py-1.5 shadow-2xl max-w-[16rem] text-[10px] leading-snug text-white relative">
                    <div className="font-medium mb-0.5">1080p Not Available</div>
                    <div className="text-gray-300">1080p is not available for google_veo-3.1-fast. Use google_veo-3.1 for 1080p support.</div>
                    <div className="absolute left-3 -bottom-0.5 w-1.5 h-1.5 bg-[#111111]/95 border border-white/10 rotate-45"></div>
                  </div>
                </div>
              )}
              {disabledDueToCredits && (
                <div className="text-[10px] text-amber-300/80 mt-1">
                  Credits are running low, you need to recharge to use all features
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  if (isPreview) return <div className={`absolute z-50 pointer-events-auto ${className}`}>{Panel}</div>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      {Panel}
    </div>
  );
};

export default VideoResolutionModal; 